import { ORIGINAL_GAME_STORE_LINK, socket } from "@/utils/utils"
import { isDemoMode } from "@mono/constants"
import { getChallengeMeta, difficultyStars } from "./challengeMeta"
import { useNavigate } from "react-router-dom"
import { useState } from "react"

interface ChallengeDetailProps {
  challenge: IChallenge
  index: number
  locked: boolean
  completed: boolean
  onPlay: () => void
}

export default function ChallengeDetail({ challenge, index, locked, completed, onPlay }: ChallengeDetailProps) {
  const { category, difficulty } = getChallengeMeta(challenge)
  const stars = difficultyStars[difficulty]
  const nav = useNavigate()
  const [isStarting, setIsStarting] = useState(false)
  const [selectedMapId, setSelectedMapId] = useState("1")
  const [selectedPlayerCount, setSelectedPlayerCount] = useState(4)

  const handleStartChallenge = async (playerCount: number) => {
    setIsStarting(true)

    socket.emit("lobby.createGame", async (gameId: string) => {
      socket.emit("lobby.hostChangeGameSettings", {
        id: gameId,
        mapId: selectedMapId,
        maxTurns: 35,
      })

      // Add bots one by one
      const botsToAdd = playerCount - 1
      for (let i = 0; i < botsToAdd; i++) {
        await new Promise((resolve) => {
          socket.emit("newgame.add-bot", gameId, (success: boolean) => {
            resolve(success)
          })
        })
      }

      socket.emit("newgame.startGame", gameId, "", challenge.id)
      nav(`/newgame/${gameId}`)
    })
  }

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-2xl mobile:rounded-[16px] border border-white/10 bg-gradient-to-b from-gray-900 via-gray-950 to-black shadow-[0_0_60px_rgba(0,0,0,0.4)]">
      <div className="challenge-scroll flex flex-1 flex-col overflow-y-auto p-8 sm:p-4 mobile:p-[16px]">
        {/* Mission number */}
        <div className="flex items-center gap-3 mobile:gap-[10px]">
          <span className="text-xs mobile:text-[11px]/[1.4] font-semibold uppercase tracking-wider text-white/40">Mission</span>
          <span className="font-fourth text-3xl mobile:text-[24px]/[1] font-bold leading-none text-amber-400">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <div className="mt-5 mobile:mt-[14px] flex items-center gap-3 mobile:gap-[10px]">
          <span className="text-xs mobile:text-[11px]/[1.4] font-semibold uppercase tracking-wider text-amber-400/70">{category}</span>
          {completed && !locked ? (
            <span className="inline-flex items-center gap-1.5 mobile:gap-[5px] rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 mobile:px-[9px] py-1 mobile:py-[3px] text-[11px] mobile:text-[11px]/[1.3] font-semibold uppercase tracking-wider text-emerald-300">
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 mobile:h-[13px] mobile:w-[13px]" fill="none" stroke="currentColor" strokeWidth="3">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              Completed
            </span>
          ) : null}
        </div>
        <h2 className="mt-2 mobile:mt-[8px] font-fourth text-3xl sm:text-2xl mobile:text-[22px]/[1.2] font-bold leading-tight tracking-wide text-white">
          {challenge.name}
        </h2>

        <div className="my-6 sm:my-4 mobile:my-[14px] h-px w-full bg-white/10" />

        <h3 className="text-xs mobile:text-[11px]/[1.4] font-semibold uppercase tracking-wider text-white/40">Objective</h3>
        <p className="mt-2.5 mobile:mt-[8px] text-[15px] sm:text-sm mobile:text-[14px]/[1.55] leading-relaxed text-white/70">{challenge.description}</p>

        {/* Difficulty */}
        <div className="mt-7 sm:mt-4 mobile:mt-[18px]">
          <h3 className="text-xs mobile:text-[11px]/[1.4] font-semibold uppercase tracking-wider text-white/40">Difficulty</h3>
          <div className="mt-3 mobile:mt-[10px] flex items-center gap-3 mobile:gap-[10px]">
            <div className="flex items-center gap-1 mobile:gap-[4px]">
              {Array.from({ length: 4 }).map((_, i) => (
                <svg
                  key={i}
                  viewBox="0 0 24 24"
                  className={`h-6 w-6 mobile:h-[22px] mobile:w-[22px] ${i < stars ? "text-amber-400" : "text-white/10"}`}
                  fill="currentColor"
                >
                  <path d="M12 2l2.9 6.26L21.6 9.3l-4.8 4.68 1.13 6.62L12 17.48 6.07 20.6l1.13-6.62-4.8-4.68 6.7-1.04L12 2z" />
                </svg>
              ))}
            </div>
            <span className="text-sm mobile:text-[13px]/[1.4] font-semibold text-white/70">{difficulty}</span>
          </div>
        </div>

        {/* Play / Locked */}
        {locked ? (
          <div className="mt-auto pt-7 sm:pt-4 mobile:pt-[18px]">
            <div className="flex w-full cursor-not-allowed items-center justify-center gap-3 mobile:gap-[10px] rounded-xl mobile:rounded-[12px] border border-amber-400/20 bg-amber-500/[0.06] px-6 py-4 sm:py-3 mobile:px-[16px] mobile:py-[12px] text-amber-200/70">
              {isDemoMode ? (
                <svg viewBox="0 0 24 24" className="h-5 w-5 mobile:h-[19px] mobile:w-[19px]" fill="currentColor">
                  <path d="M12 1a5 5 0 00-5 5v3H6a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2v-8a2 2 0 00-2-2h-1V6a5 5 0 00-5-5zm3 8H9V6a3 3 0 016 0v3z" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" className="h-5 w-5 mobile:h-[19px] mobile:w-[19px]" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="9" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 2" />
                </svg>
              )}
              <span className="font-fourth text-lg mobile:text-[16px]/[1.25] font-bold tracking-wide">
                {isDemoMode ? "Locked in Demo" : "Coming Soon"}
              </span>
            </div>
            {isDemoMode ? (
              <a
                href={ORIGINAL_GAME_STORE_LINK}
                target="_blank"
                rel="noreferrer"
                className="mt-3 mobile:mt-[10px] flex w-full items-center justify-center gap-2 mobile:gap-[8px] rounded-xl mobile:rounded-[12px] border border-amber-400/40 bg-amber-500/10 px-6 py-3 mobile:px-[16px] mobile:py-[11px] text-amber-200 transition-all duration-200 hover:border-amber-400/70 hover:bg-amber-500/20"
              >
                <span className="text-sm mobile:text-[12px]/[1.3] font-semibold uppercase tracking-wide">Unlock with Full Version</span>
                <span>&rarr;</span>
              </a>
            ) : (
              <p className="mt-3 mobile:mt-[10px] text-center text-sm mobile:text-[12px]/[1.45] text-white/40">This mission is not playable yet — check back soon.</p>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => handleStartChallenge(4)}
            className="mt-auto mobile:mt-[18px] flex w-full items-center justify-center gap-3 mobile:gap-[10px] rounded-xl mobile:rounded-[12px] border-2 border-amber-400/60 bg-gradient-to-r from-amber-500 via-yellow-600 to-amber-600 px-6 py-4 sm:py-3 mobile:px-[16px] mobile:py-[13px] text-white shadow-xl transition-all duration-200 hover:from-amber-600 hover:via-yellow-600 hover:to-amber-700 hover:scale-[1.02] active:scale-100 focus:outline-none focus:ring-4 focus:ring-amber-300/50"
          >
            <svg viewBox="0 0 24 24" className="h-6 w-6 mobile:h-[20px] mobile:w-[20px]" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
            <span className="font-fourth text-xl sm:text-lg mobile:text-[17px]/[1.25] font-bold tracking-wide">
              {completed ? "Play Again" : "Play Challenge"}
            </span>
          </button>
        )}
      </div>
    </div>
  )
}
